import { Box, Card, CardHeader, Collapse, Divider, Stack, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';
import { moneyFormat } from 'modules/common/display';

type Props = {
    children: ReactNode;
    title: string;
    reimbursableAmount: number;
    subtitle?: string;
    description?: ReactNode | string;
    isCollapsible?: boolean;
};

export default function SectionCard({
    title,
    subtitle,
    description,
    reimbursableAmount,
    children,
    isCollapsible,
}: Props) {
    const [isOpen, setIsOpen] = useState(true);

    const toggleOpen = () => {
        if (!isCollapsible) {
            return;
        }
        setIsOpen(!isOpen);
    };

    return (
        <Card>
            <Stack>
                <CardHeader
                    title={
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack>
                                <Typography variant="h4">{title}</Typography>
                                {subtitle && (
                                    <Typography variant="body2" color="textSecondary">
                                        {subtitle}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography variant="h4">
                                    {moneyFormat(reimbursableAmount || 0, { minPrecision: 2 })}
                                </Typography>
                                {isCollapsible &&
                                    (isOpen ? (
                                        <ChevronUpIcon fontSize="small" />
                                    ) : (
                                        <ChevronDownIcon fontSize="small" />
                                    ))}
                            </Stack>
                        </Stack>
                    }
                    onClick={toggleOpen}
                    sx={{
                        '&:hover': {
                            backgroundColor: isCollapsible ? 'cement.50' : 'transparent',
                        },
                        cursor: isCollapsible ? 'pointer' : 'default',
                    }}
                />
                <Collapse in={isOpen}>
                    <Divider />
                    {typeof description === 'string' ? (
                        <Box bgcolor="cement.50" py={2} px={3}>
                            <Typography variant="body2">{description}</Typography>
                        </Box>
                    ) : (
                        description
                    )}
                    {children}
                </Collapse>
            </Stack>
        </Card>
    );
}
